import React, { useState, useEffect } from 'react';
import './AdminApproval.css';

const AdminApproval = () => {
  const [requests, setRequests] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [modifiedRequests, setModifiedRequests] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locationsRes, requestsRes] = await Promise.all([
          fetch('/api/locations/getAllLocations'),
          fetch('/api/hygiene/requests?status=APPROVED')
        ]);
        
        const locationsData = await locationsRes.json();
        const requestsData = await requestsRes.json();
        
        setLocations(locationsData);
        setRequests(requestsData);
        
        // Initialiser les modifications avec commentaire admin
        const initialModifications = {};
        requestsData.forEach(request => {
          initialModifications[request._id] = {
            quantity: request.quantity,
            price: request.price || 0,
            adminComment: '' // Nouveau champ pour commentaire admin
          };
        });
        setModifiedRequests(initialModifications);
      } catch (error) {
        console.error('Erreur chargement données:', error);
      }
    };

    fetchData();
  }, []);

  const handleQuantityChange = (requestId, newQuantity) => {
    if (newQuantity < 1) newQuantity = 1;
    
    setModifiedRequests(prev => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        quantity: newQuantity
      }
    }));
  };

  const incrementQuantity = (requestId) => {
    const currentQuantity = modifiedRequests[requestId]?.quantity || 1;
    handleQuantityChange(requestId, currentQuantity + 1);
  };

  const decrementQuantity = (requestId) => {
    const currentQuantity = modifiedRequests[requestId]?.quantity || 1;
    if (currentQuantity > 1) {
      handleQuantityChange(requestId, currentQuantity - 1);
    }
  };

  const handleAdminCommentChange = (requestId, adminComment) => {
    setModifiedRequests(prev => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        adminComment: adminComment
      }
    }));
  };

  const updateRequestStatus = async (requestId, status) => {
    try {
      const modifications = modifiedRequests[requestId] || {};
      const originalRequest = requests.find(req => req._id === requestId);
      
      const response = await fetch(`/api/hygiene/requests/${requestId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          status,
          quantity: modifications.quantity || originalRequest.quantity,
          price: modifications.price || originalRequest.price || 0,
          confirmedBy: localStorage.getItem('username') || 'Admin',
          adminComment: modifications.adminComment || '' // Envoyer le commentaire admin
        }),
      });
      
      if (response.ok) {
        const updatedRequest = await response.json();
        setRequests(prev => prev.map(req => 
          req._id === requestId ? updatedRequest : req
        ));
        
        // Supprimer les modifications pour cette demande
        setModifiedRequests(prev => {
          const newModifications = { ...prev };
          delete newModifications[requestId];
          return newModifications;
        });

        if (status === 'CONFIRMED') {
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 3000);
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur mise à jour demande:', error);
      return false;
    }
  };

  const confirmAllRequests = async () => {
    const requestsToConfirm = selectedLocation 
      ? requests.filter(req => req.location === selectedLocation && req.status === 'APPROVED')
      : requests.filter(req => req.status === 'APPROVED');

    const results = await Promise.all(
      requestsToConfirm.map(request =>
        updateRequestStatus(request._id, 'CONFIRMED')
      )
    );

    const successCount = results.filter(result => result).length;
    
    if (successCount === requestsToConfirm.length) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } else {
      alert(`Seulement ${successCount} sur ${requestsToConfirm.length} demandes ont été confirmées.`);
    }
  };

  const filteredRequests = selectedLocation 
    ? requests.filter(req => req.location === selectedLocation && req.status === 'APPROVED')
    : requests.filter(req => req.status === 'APPROVED');

  const getQuantityChange = (request) => {
    const original = request.quantity;
    const modified = modifiedRequests[request._id]?.quantity || original;
    const difference = modified - original;
    
    if (difference > 0) return { type: 'increase', value: difference };
    if (difference < 0) return { type: 'decrease', value: Math.abs(difference) };
    return { type: 'same', value: 0 };
  };

  return (
    <div className="admin-approval-container">
      <div className="approval-header">
        <h2>Approbation Administrative</h2>
        <p>Modifiez les quantités si nécessaire avant confirmation</p>
      </div>

      {showSuccess && (
        <div className="success-message">
          ✅ Demandes confirmées avec succès
        </div>
      )}
      
      <div className="controls-section">
        <select
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          className="location-select"
        >
          <option value="">Toutes les localisations</option>
          {locations.map(location => (
            <option key={location._id} value={location.name}>
              {location.name}
            </option>
          ))}
        </select>

        <button
          onClick={confirmAllRequests}
          disabled={filteredRequests.length === 0}
          className="confirm-all-btn"
        >
          Tout Confirmer ({filteredRequests.length})
        </button>
      </div>

      <div className="requests-grid">
        {filteredRequests.length > 0 ? (
          filteredRequests.map(request => {
            const quantityChange = getQuantityChange(request);
            const modifications = modifiedRequests[request._id] || {};
            
            return (
              <div key={request._id} className="request-card">
                <div className="request-info">
                  <h4 className="product-name">{request.productName}</h4>
                  <div className="request-details">
                    <span className="location">{request.location}</span>
                    <span className="requester">Par: {request.requestedBy}</span>
                    <span className="approval-date">
                      Approuvé le: {request.approvedAt ? new Date(request.approvedAt).toLocaleDateString('fr-FR') : 'N/A'}
                    </span>
                    <span className="approver">
                      Par: {request.approvedBy || 'Contrôleur'}
                    </span>
                  </div>
                </div>

                {/* Section des commentaires existants */}
                <div className="comments-section">
                  {/* Commentaire de l'utilisateur */}
                  {request.comments && (
                    <div className="comment-group">
                      <div className="comment-label">Commentaire utilisateur:</div>
                      <div className="comment-content user-comment">
                        {request.comments}
                      </div>
                    </div>
                  )}

                  {/* Commentaire du contrôleur */}
                  {request.modificationReason && (
                    <div className="comment-group">
                      <div className="comment-label">Commentaire contrôleur:</div>
                      <div className="comment-content controller-comment">
                        {request.modificationReason}
                      </div>
                    </div>
                  )}
                </div>

                <div className="quantity-section">
                  <div className="quantity-controls">
                    <button
                      type="button"
                      className="quantity-btn"
                      onClick={() => decrementQuantity(request._id)}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={modifications.quantity || request.quantity}
                      onChange={(e) => handleQuantityChange(request._id, parseInt(e.target.value) || 1)}
                      className="quantity-input"
                    />
                    <button
                      type="button"
                      className="quantity-btn"
                      onClick={() => incrementQuantity(request._id)}
                    >
                      +
                    </button>
                  </div>
                  <div className="unit-display">{request.unit}</div>
                  {quantityChange.type !== 'same' && (
                    <div className={`quantity-change ${
                      quantityChange.type === 'increase' ? 'quantity-increased' : 'quantity-decreased'
                    }`}>
                      {quantityChange.type === 'increase' ? '↑' : '↓'} 
                      {quantityChange.value}
                    </div>
                  )}
                </div>

                <div className="price-section">
                  <span className="price-label">Prix: </span>
                  <span className="price-value">{request.price || 0} DT</span>
                </div>

                {/* Commentaire de l'admin */}
                <div className="admin-comment-section">
                  <div className="comment-label">Commentaire admin (optionnel):</div>
                  <textarea
                    placeholder="Ajoutez un commentaire si nécessaire..."
                    value={modifications.adminComment || ''}
                    onChange={(e) => handleAdminCommentChange(request._id, e.target.value)}
                    className="admin-comment-textarea"
                  />
                </div>

                <div className="action-buttons">
                  <button
                    onClick={() => updateRequestStatus(request._id, 'CONFIRMED')}
                    className="confirm-btn"
                  >
                    Confirmer
                  </button>
                  <button
                    onClick={() => updateRequestStatus(request._id, 'REJECTED')}
                    className="reject-btn"
                  >
                    Rejeter
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-state">
            <p>Aucune demande approuvée en attente de confirmation</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminApproval;